import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Landmark, ShieldCheck, ReceiptText, Users, Compass, Zap, CheckCheck,
  ArrowRight, ArrowLeft, Check, Rocket, Slack, Mail, MessageSquare, Phone, Blocks,
} from "lucide-react";
import { AriaMark } from "@/components/AriaMark";

/* ---------------------------------------------------------------- previews */
const Tile = ({ children, color }) => (
  <div className="flex items-center gap-2 rounded-xl border px-3 py-2.5 text-[12px] font-medium"
    style={{ borderColor: `${color}33`, background: `${color}12`, color }}>
    {children}
  </div>
);

const ConnectPreview = () => (
  <div className="w-full space-y-3">
    <div className="rounded-2xl border border-[rgba(79,142,247,0.28)] bg-[rgba(79,142,247,0.06)] p-4">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[rgba(79,142,247,0.14)] text-[#4F8EF7]"><Landmark size={17} /></span>
        <div className="flex-1">
          <div className="text-[12.5px] font-medium text-white">Bank & card connections</div>
          <div className="text-[10.5px] text-[#6b6b6b]">Plaid · live transaction feed</div>
        </div>
        <span className="rounded-lg bg-[#4F8EF7] px-2.5 py-1 text-[11px] font-medium text-white">Connect</span>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-2.5">
      <Tile color="#22C55E"><Blocks size={14} /> QuickBooks</Tile>
      <Tile color="#8B5CF6"><Slack size={14} /> Slack</Tile>
      <Tile color="#06B6D4"><Blocks size={14} /> NetSuite</Tile>
      <Tile color="#F59E0B"><Blocks size={14} /> Ramp</Tile>
    </div>
  </div>
);

const PolicyPreview = () => (
  <div className="w-full space-y-3">
    <div className="rounded-2xl border border-[#222] bg-[#0d0d0d] p-3.5 text-[12.5px] text-[#e5e5e5]">
      "Meals over <span className="text-[#7FAEF9]">$75 per person</span> need manager approval."
    </div>
    <div className="flex justify-center text-[#4F8EF7]"><ArrowRight size={16} className="rotate-90" /></div>
    <div className="rounded-2xl border border-[rgba(34,197,94,0.25)] bg-[rgba(34,197,94,0.05)] p-3.5">
      <div className="flex items-center justify-between">
        <span className="text-[12.5px] font-medium text-white">Meal limit · $75 / person</span>
        <span className="rounded-full border border-[rgba(245,158,11,0.25)] bg-[rgba(245,158,11,0.12)] px-2 py-0.5 text-[10px] text-[#FBBF24]">require_approval</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5 text-[10px] text-[#A3A3A3]">
        <span className="rounded-md bg-[#161616] px-1.5 py-0.5">category = Meals</span>
        <span className="rounded-md bg-[#161616] px-1.5 py-0.5">amount &gt; 75</span>
        <span className="rounded-md bg-[#161616] px-1.5 py-0.5">applies to all</span>
      </div>
    </div>
  </div>
);

const AutopilotPreview = () => {
  const stages = ["Receipt read", "Policy checked", "GL 6010 assigned", "Auto-approved"];
  return (
    <div className="w-full space-y-2.5">
      {stages.map((s, i) => (
        <div key={s} className="flex items-center gap-2.5 rounded-xl border border-[#222] bg-[#0d0d0d] px-3.5 py-2.5">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[rgba(34,197,94,0.14)] text-[#4ADE80]"><Check size={11} /></span>
          <span className="flex-1 text-[12px] text-[#e5e5e5]">{s}</span>
        </div>
      ))}
    </div>
  );
};

const TeamPreview = () => (
  <div className="w-full space-y-2.5">
    <div className="flex items-start gap-2">
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center"><AriaMark size={24} /></span>
      <div className="rounded-2xl border border-[#222] bg-[#141414] px-3 py-2 text-[11.5px] text-[#e5e5e5]">
        <span className="mb-1 inline-flex items-center gap-1 rounded-full border border-[rgba(139,92,246,0.28)] bg-[rgba(139,92,246,0.12)] px-1.5 text-[9px] text-[#A78BFA]"><Slack size={8} /> Slack</span>
        <div>Hi Noah — still need your Uber receipt from SFO. Drop it here anytime.</div>
      </div>
    </div>
    <div className="flex flex-row-reverse items-start gap-2">
      <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#222] text-[8px] font-bold text-[#c9c9c9]">NK</span>
      <div className="rounded-2xl border border-[#2a2a2a] bg-[#1f1f1f] px-3 py-2 text-[11.5px] text-[#e5e5e5]">On it, sending now.</div>
    </div>
    <div className="flex items-center gap-1.5 rounded-xl border border-[rgba(245,158,11,0.28)] bg-[rgba(245,158,11,0.08)] px-3 py-2 text-[10.5px] text-[#FBBF24]">
      Take over · send via {" "}
      <span className="inline-flex items-center gap-1"><Slack size={10} /></span>
      <span className="inline-flex items-center gap-1"><MessageSquare size={10} /></span>
      <span className="inline-flex items-center gap-1"><Mail size={10} /></span>
      <span className="inline-flex items-center gap-1"><Phone size={10} /></span>
    </div>
  </div>
);

const CommandPreview = () => (
  <div className="w-full grid grid-cols-1 gap-2.5">
    <div className="rounded-2xl border border-[rgba(79,142,247,0.25)] bg-[rgba(79,142,247,0.05)] p-3.5">
      <div className="flex items-center gap-1.5 text-[12px] font-medium text-white"><Compass size={13} className="text-[#4F8EF7]" /> Organizer</div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <span className="rounded-full border border-[#2a2a2a] bg-[#161616] px-2 py-0.5 text-[10px] text-[#A3A3A3]">Who has violations?</span>
        <span className="rounded-full border border-[#2a2a2a] bg-[#161616] px-2 py-0.5 text-[10px] text-[#A3A3A3]">Most violated policies</span>
      </div>
    </div>
    <div className="rounded-2xl border border-[rgba(245,158,11,0.25)] bg-[rgba(245,158,11,0.05)] p-3.5">
      <div className="flex items-center gap-1.5 text-[12px] font-medium text-white"><Zap size={13} className="text-[#FBBF24]" /> Task Master</div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <span className="rounded-full border border-[#2a2a2a] bg-[#161616] px-2 py-0.5 text-[10px] text-[#A3A3A3]">Nudge missing receipts</span>
        <span className="rounded-full border border-[#2a2a2a] bg-[#161616] px-2 py-0.5 text-[10px] text-[#A3A3A3]">Auto-approve clean</span>
      </div>
    </div>
  </div>
);

const ApprovalsPreview = () => {
  const cols = [["Needs review", "#7FAEF9", 3], ["Approved", "#4ADE80", 8], ["Exported", "#A78BFA", 5]];
  return (
    <div className="grid w-full grid-cols-3 gap-2.5">
      {cols.map(([label, color, n]) => (
        <div key={label} className="rounded-xl border border-[#222] bg-[#0d0d0d] p-2.5">
          <div className="mb-2 flex items-center gap-1.5 text-[10.5px] font-medium" style={{ color }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} /> {label}
          </div>
          <div className="space-y-1.5">
            {[...Array(2)].map((_, i) => <div key={i} className="h-6 rounded-md bg-[#161616]" />)}
            <div className="text-[9.5px] text-[#555]">+{n}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

const WelcomePreview = () => (
  <div className="flex flex-col items-center justify-center">
    <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4 }}
      className="flex h-24 w-24 items-center justify-center">
      <AriaMark size={96} />
    </motion.div>
    <div className="mt-4 text-[13px] text-[#A3A3A3]">Agentic Travel & Expense</div>
  </div>
);

const DonePreview = () => (
  <div className="flex flex-col items-center justify-center">
    <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="flex h-24 w-24 items-center justify-center rounded-3xl border border-[rgba(34,197,94,0.3)] bg-[rgba(34,197,94,0.12)]">
      <CheckCheck size={40} className="text-[#4ADE80]" />
    </motion.div>
    <div className="mt-4 text-[13px] text-[#A3A3A3]">Workspace live · real data loaded</div>
  </div>
);

/* ---------------------------------------------------------------- steps */
const STEPS = [
  { id: "welcome", icon: AriaMark, eyebrow: "Welcome to Aria", title: "Your AI finance controller", accent: "#4F8EF7",
    body: "Aria runs travel & expense end to end — from the bank feed to the general ledger. Take a 60-second tour of everything you can do.",
    Preview: WelcomePreview },
  { id: "connect", icon: Landmark, eyebrow: "Step 1 · Connect", title: "Bring in real spend", accent: "#4F8EF7",
    body: "Link a bank or corporate card through Plaid and connect your stack — QuickBooks, NetSuite, Slack and more. Aria pulls every transaction automatically.",
    Preview: ConnectPreview },
  { id: "policies", icon: ShieldCheck, eyebrow: "Step 2 · Policies", title: "Write policy in plain English", accent: "#22C55E",
    body: "Type a rule the way you'd say it. Aria turns it into a structured, enforced policy and simulates the impact before you publish.",
    Preview: PolicyPreview },
  { id: "autopilot", icon: ReceiptText, eyebrow: "Step 3 · Autopilot", title: "Aria files every expense", accent: "#4ADE80",
    body: "Drop a receipt or let the card feed flow in. Aria reads it, checks policy, assigns the GL code and auto-approves the clean ones — no forms, ever.",
    Preview: AutopilotPreview },
  { id: "team", icon: Users, eyebrow: "Step 4 · Your team", title: "Meet people where they are", accent: "#8B5CF6",
    body: "Aria nudges employees over Slack, email or text. Open anyone's thread to see how they're performing — and take over the conversation yourself, even log a call.",
    Preview: TeamPreview },
  { id: "command", icon: Compass, eyebrow: "Step 5 · Command Center", title: "Ask, or tell Aria to act", accent: "#4F8EF7",
    body: "Organizer answers questions like 'who has outstanding violations?' with live cards. Task Master nudges people, auto-approves clean expenses and tracks the work.",
    Preview: CommandPreview },
  { id: "approvals", icon: CheckCheck, eyebrow: "Step 6 · Approvals", title: "One-tap approvals", accent: "#A78BFA",
    body: "Everything that still needs a human lands in a clean queue and kanban board. Approve, request info, or let Aria handle the routine.",
    Preview: ApprovalsPreview },
  { id: "done", icon: Rocket, eyebrow: "You're all set", title: "Aria is watching your spend", accent: "#4ADE80",
    body: "That's the whole platform. Your workspace is already live with real data — jump in and let Aria take it from here.",
    Preview: DonePreview },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  const step = STEPS[i];
  const isLast = i === STEPS.length - 1;
  const Icon = step.icon;

  const finish = () => navigate("/expenses");
  const next = () => (isLast ? finish() : setI((v) => v + 1));
  const back = () => setI((v) => Math.max(0, v - 1));

  return (
    <div className="relative min-h-screen bg-[#0A0A0A] text-white" data-testid="onboarding-page">
      {/* subtle top glow */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-64" style={{ background: `radial-gradient(60% 100% at 50% 0%, ${step.accent}14, transparent)` }} />

      {/* top bar */}
      <div className="relative mx-auto flex w-full max-w-5xl items-center justify-between px-6 py-6">
        <div className="flex items-center gap-2.5">
          <AriaMark size={32} />
          <div className="text-[15px] font-semibold tracking-tight">Aria</div>
        </div>
        <button onClick={finish} data-testid="onboarding-skip"
          className="text-[12px] text-[#6b6b6b] transition-colors hover:text-white">Skip tour</button>
      </div>

      {/* content */}
      <div className="relative mx-auto flex w-full max-w-5xl flex-col items-stretch gap-8 px-6 pb-10 pt-4 lg:flex-row lg:items-center">
        {/* left: copy */}
        <div className="flex-1">
          <AnimatePresence mode="wait">
            <motion.div key={step.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.28 }} data-testid={`onboarding-step-${step.id}`}>
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl border" style={{ borderColor: `${step.accent}44`, background: `${step.accent}18`, color: step.accent }}>
                <Icon size={22} />
              </div>
              <div className="mt-5 text-[11.5px] font-semibold uppercase tracking-wider" style={{ color: step.accent }}>{step.eyebrow}</div>
              <h1 className="mt-2 text-[30px] font-semibold leading-tight tracking-tight lg:text-[36px]">{step.title}</h1>
              <p className="mt-3 max-w-md text-[14px] leading-relaxed text-[#A3A3A3]">{step.body}</p>
            </motion.div>
          </AnimatePresence>

          {/* controls */}
          <div className="mt-8 flex items-center gap-3">
            {i > 0 && (
              <button onClick={back} data-testid="onboarding-back"
                className="flex h-10 items-center gap-1.5 rounded-xl border border-[#2a2a2a] px-4 text-[13px] text-[#A3A3A3] transition-colors hover:bg-[#161616] hover:text-white">
                <ArrowLeft size={15} /> Back
              </button>
            )}
            <button onClick={next} data-testid={isLast ? "onboarding-finish" : "onboarding-next"}
              className="flex h-10 items-center gap-1.5 rounded-xl btn-gradient px-5 text-[13px] font-medium text-white transition-colors">
              {isLast ? <>Enter Aria <Rocket size={15} /></> : <>Continue <ArrowRight size={15} /></>}
            </button>
          </div>

          {/* progress */}
          <div className="mt-8 flex items-center gap-2" data-testid="onboarding-progress">
            {STEPS.map((s, idx) => (
              <button key={s.id} onClick={() => setI(idx)} aria-label={`Go to ${s.id}`}
                className="h-1.5 rounded-full transition-all"
                style={{ width: idx === i ? 28 : 8, background: idx <= i ? step.accent : "#2a2a2a" }} />
            ))}
            <span className="ml-2 text-[11px] tabular-nums text-[#555]">{i + 1} / {STEPS.length}</span>
          </div>
        </div>

        {/* right: preview panel */}
        <div className="flex-1">
          <div className="rounded-[20px] border border-[#222] bg-[#111111] p-6 card-shadow" style={{ minHeight: 340 }}>
            <AnimatePresence mode="wait">
              <motion.div key={step.id} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.28 }} className="flex h-full min-h-[300px] items-center justify-center">
                <step.Preview />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
